import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

type TicketStatus = 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'medium' | 'high' | 'critical';

interface TicketFilters {
  status?: TicketStatus | TicketStatus[];
  priority?: TicketPriority;
  assigned_to?: string;
  customer_id?: string;
  location_id?: string;
}

export interface AdminTicket {
  id: string;
  ticket_number: string;
  customer_id: string;
  location_id: string | null;
  deployment_id: string | null;
  asset_id: string | null;
  title: string;
  description: string | null;
  status: TicketStatus | null;
  priority: TicketPriority | null;
  source: string | null;
  sla_deadline: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  customer: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  deployment: { id: string; solution_type: string } | null;
  assigned_to: { id: string; full_name: string | null; email: string } | null;
}

export function useAllTickets(filters?: TicketFilters) {
  return useQuery({
    queryKey: ['admin-tickets', filters],
    queryFn: async () => {
      let query = supabase
        .from('tickets')
        .select(`
          id, ticket_number, customer_id, location_id, deployment_id, asset_id,
          title, description, status, priority, source, sla_deadline,
          resolution_notes, resolved_at, created_at, updated_at,
          customer:customers(id, name),
          location:locations(id, name),
          deployment:deployments(id, solution_type),
          assigned_to:users!tickets_assigned_to_user_id_fkey(id, full_name, email)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }
      if (filters?.priority) query = query.eq('priority', filters.priority);
      if (filters?.assigned_to) query = query.eq('assigned_to_user_id', filters.assigned_to);
      if (filters?.customer_id) query = query.eq('customer_id', filters.customer_id);
      if (filters?.location_id) query = query.eq('location_id', filters.location_id);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as AdminTicket[];
    },
    staleTime: 30 * 1000,
  });
}
